/**
 * Coaching Notes Page
 *
 * Supervisor view for managing coaching notes.
 * Features:
 * - List of all sent notes
 * - Filter by agent
 * - Create new note
 */

import { createFileRoute } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useRole } from '@/contexts/RoleContext';
import { canWriteCoaching } from '@aica/core/rbac';
import { formatDateShort } from '@/lib/utils';
import { useApiClient } from '@/hooks/useApiClient';
import { logError } from '@/lib/logger';
import { distinctByAgentId } from '@/lib/employee-row-key';
import { PageBody } from '@/components/layout/PageBody';
import { PageHeader } from '@/components/layout/PageHeader';
import { Breadcrumb, BreadcrumbItem } from '@/components/navigation/breadcrumb';
import { Button } from '@/components/ui/button';
import { TableSkeleton } from '@/components/ui/skeletons';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { SupervisorNoteModal, type SupervisorNoteType } from '@/components/supervisor';
import { CoachingEmptyHint } from '@/components/coaching/CoachingEmptyHint';
import { Plus, User, Filter, GraduationCap } from 'lucide-react';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import type { SupervisorNote, SupervisorNotesResponse } from '@/lib/api';
import { parseSupervisorNoteType } from '@/lib/utils/supervisor-note-type';

const coachingNotesQueryKey = (orgId: string | null) =>
  ['coaching', 'notes', orgId] as const;

const coachingAgentsQueryKey = (orgId: string | null) =>
  ['coaching', 'agents', orgId] as const;

export const Route = createFileRoute('/coaching/notes')({
  component: CoachingNotesPage,
});

function CoachingNotesPage() {
  const { t } = useTranslation(['dashboard', 'common']);
  useDocumentTitle(t('coaching.notes.title'));
  const { selectedOrgId, globalRole, isOwner } = useRole();
  // Writing a note is role ∧ grant (`agent.create`): a grant opens this page
  // for reading only (#1645).
  const canWrite = canWriteCoaching(globalRole, isOwner);
  const { apiClient } = useApiClient();
  const queryClient = useQueryClient();

  const [selectedAgentId, setSelectedAgentId] = useState<string>('all');

  // Modal state
  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    type: SupervisorNoteType;
    agentId: string;
    agentName?: string;
  }>({ isOpen: false, type: 'coaching', agentId: '' });

  const notesQuery = useQuery({
    queryKey: coachingNotesQueryKey(selectedOrgId),
    queryFn: () => apiClient.agents.getSentNotes({ limit: 50 }),
    enabled: !!selectedOrgId,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
  const agentsQuery = useQuery({
    queryKey: coachingAgentsQueryKey(selectedOrgId),
    // The agent picker lists every agent, not the top 100 by score (#1140).
    queryFn: () => apiClient.agents.getTeamLeaderboard({ scope: 'team' }),
    enabled: !!selectedOrgId,
    staleTime: 5 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const notes = useMemo<SupervisorNote[]>(
    () => notesQuery.data?.notes ?? [],
    [notesQuery.data?.notes]
  );
  const agents = useMemo(
    () =>
      distinctByAgentId(agentsQuery.data?.entries ?? []).map((entry) => ({
        id: entry.agentId,
        name: entry.agentName,
      })),
    [agentsQuery.data]
  );
  const loading = notesQuery.isLoading || agentsQuery.isLoading;
  const error = notesQuery.error || agentsQuery.error;

  useEffect(() => {
    if (!error) return;
    logError(
      t('coaching.notes.failedToFetch'),
      {},
      error instanceof Error ? error : undefined
    );
  }, [error, t]);

  // Filter notes locally
  const filteredNotes = useMemo(
    () =>
      notes.filter(
        (note) => selectedAgentId === 'all' || note.agentId === selectedAgentId
      ),
    [notes, selectedAgentId]
  );

  const openCreateModal = () => {
    if (selectedAgentId === 'all') {
      if (agents.length > 0 && agents[0]) {
        setModalState({
          isOpen: true,
          type: 'coaching',
          agentId: agents[0].id,
          agentName: agents[0].name,
        });
      }
    } else {
      const agent = agents.find((a) => a.id === selectedAgentId);
      setModalState({
        isOpen: true,
        type: 'coaching',
        agentId: selectedAgentId,
        agentName: agent?.name,
      });
    }
  };

  const pageHeader = (actions?: ReactNode) => (
    <PageHeader
      sticky
      breadcrumbs={
        <Breadcrumb>
          <BreadcrumbItem href="/">Aelo</BreadcrumbItem>
          <BreadcrumbItem current>
            {t('coaching.notes.title', 'Coaching Notes')}
          </BreadcrumbItem>
        </Breadcrumb>
      }
      icon={GraduationCap}
      title={t('coaching.notes.title', 'Coaching Notes')}
      subtitle={t('coaching.notes.description')}
      actions={actions}
    />
  );

  if (loading && notes.length === 0) {
    return (
      <PageBody>
        {pageHeader()}
        <TableSkeleton />
      </PageBody>
    );
  }

  return (
    <PageBody>
      {pageHeader(
        canWrite && selectedAgentId !== 'all' ? (
          <Button onClick={openCreateModal}>
            <Plus className="w-4 h-4 mr-2" />
            {t('coaching.createNote')}
          </Button>
        ) : undefined
      )}
      <div className="space-y-6">
        {/* Filters */}
        <div className="flex items-center gap-4 bg-bg-surface p-4 rounded-card border border-border">
          <div className="flex items-center gap-2 text-fg-muted">
            <Filter className="w-4 h-4" />
            <span className="text-sm font-medium">{t('common:common.filterBy')}:</span>
          </div>

          <div className="w-64">
            <Select
              value={selectedAgentId}
              onValueChange={(val) => setSelectedAgentId(val as string)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('coaching.notes.selectAgent')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('coaching.notes.allAgents')}</SelectItem>
                {agents.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {canWrite && selectedAgentId === 'all' && (
            <span className="text-xs text-fg-muted ml-auto">
              {t('coaching.notes.selectAgentForNewNote')}
            </span>
          )}
        </div>

        {/* Notes Table */}
        <div className="rounded-md border border-border bg-bg-surface overflow-hidden">
          <div className="w-full text-sm text-left">
            <table className="w-full">
              <caption className="sr-only">
                {t('coaching.notes.tableCaption', 'Coaching notes')}
              </caption>
              <thead className="bg-bg-elevated text-xs uppercase text-fg-muted font-medium border-b border-border">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left">
                    {t('coaching.notes.table.date')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-left">
                    {t('coaching.notes.table.agent')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-left">
                    {t('coaching.notes.table.type')}
                  </th>
                  <th scope="col" className="px-4 py-3 text-left">
                    {t('coaching.notes.table.message')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-bg-elevated">
                {filteredNotes.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-fg-muted">
                      {notes.length === 0 ? (
                        <CoachingEmptyHint
                          kind="notes"
                          title={t('coaching.notes.noNotesFound')}
                          failed={Boolean(notesQuery.error)}
                          readOnly={!canWrite}
                        />
                      ) : (
                        t('coaching.notes.noNotesFound')
                      )}
                    </td>
                  </tr>
                ) : (
                  filteredNotes.map((note) => {
                    const { type, cleanContent } = parseSupervisorNoteType(note.content);
                    return (
                      <tr
                        key={note.id}
                        className="hover:bg-bg-elevated/50 transition-colors"
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-fg-muted">
                          {formatDateShort(note.createdAt)}
                        </td>
                        <td className="px-4 py-3 font-medium text-fg">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-fg-muted" />
                            {note.agentName || t('coaching.notes.unknownAgent')}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={
                              type === 'recognition'
                                ? 'success'
                                : type === 'schedule'
                                  ? 'primary'
                                  : 'default'
                            }
                            outline
                          >
                            {type}
                          </Badge>
                        </td>
                        <td
                          className="px-4 py-3 max-w-md truncate text-fg"
                          title={cleanContent}
                        >
                          {cleanContent}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal */}
      <SupervisorNoteModal
        isOpen={modalState.isOpen}
        type={modalState.type}
        agentId={modalState.agentId}
        agentName={modalState.agentName}
        onClose={() => setModalState((prev) => ({ ...prev, isOpen: false }))}
        onSuccess={(createdNote) => {
          if (!selectedOrgId) return;
          queryClient.setQueryData<SupervisorNotesResponse>(
            coachingNotesQueryKey(selectedOrgId),
            (previous) => {
              if (!previous) {
                return { notes: [createdNote], total: 1 };
              }

              const exists = previous.notes.some((note) => note.id === createdNote.id);
              if (exists) return previous;

              return {
                ...previous,
                notes: [createdNote, ...previous.notes].slice(0, 50),
                total: previous.total + 1,
              };
            }
          );
        }}
      />
    </PageBody>
  );
}
